import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LibraryService } from '../services/library.service.js';
import { TwitchApiService, TwitchVideoData, HelixClip, ClipsPeriod, ChannelSearchResult } from '../services/twitch-api.service.js';
import { TwitchChatService } from '../services/twitch-chat.service.js';
import { TwitchChatStateService } from '../services/twitch-chat-state.service.js';
import { FirebaseService } from '../services/firebase.service.js';
import { SavedChannel, StreamProject } from '../models/library.model.js';
import { TooltipDirective } from '../directives/tooltip.directive.js';

@Component({
    selector: 'app-channel-library-overlay',
    standalone: true,
    imports: [CommonModule, TooltipDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:click)': 'onDocumentClick()',
        '(document:keydown.escape)': 'onEscape()',
    },
    template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 md:p-8 animate-fade-in"
         (click)="close.emit()">

      <div class="w-full max-w-6xl h-full max-h-[90vh] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl flex flex-col md:flex-row overflow-hidden relative"
           (click)="$event.stopPropagation()">
           
        <!-- Close Button -->
        <button (click)="close.emit()" class="absolute top-2 right-2 md:top-4 md:right-4 z-10 p-2 rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" appTooltip="Закрыть">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <!-- Left Sidebar: Saved Channels. Below md: full-width block stacked
             above the VOD grid (channel list becomes a horizontal chip
             scroller, see below); at md+ identical to the original fixed
             w-72 column. -->
        <div class="w-full md:w-72 bg-gray-950 flex flex-col border-b md:border-b-0 md:border-r border-gray-800 shrink-0">
            <div class="p-3 md:p-5 border-b border-gray-800">
                <h2 class="text-xl font-bold tracking-tight text-white mb-3 md:mb-4">Библиотека</h2>
                
                <!-- Add Channel — input + autocomplete dropdown. Wrapper is "relative" so the
                     absolute popover anchors to row width. mousedown.preventDefault on the
                     dropdown wrapper keeps focus on input during item click, so blur-close
                     (150ms) does not race the click handler. -->
                <div class="relative mb-2">
                    <div class="flex gap-2">
                        <input type="text" placeholder="Имя канала..."
                               class="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-indigo-500 outline-none placeholder-gray-500"
                               [value]="channelQuery()"
                               (input)="onQueryInput($event)"
                               (focus)="onInputFocus()"
                               (blur)="onInputBlur()"
                               (keydown)="onQueryKeydown($event)">
                        <button (click)="addChannel(channelQuery()); closeDropdown()"
                                [disabled]="isFetchingChannel()"
                                class="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                                appTooltip="Добавить канал по точному login (если автокомплит не нашёл)">
                            @if (isFetchingChannel()) {
                                <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            } @else {
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" /></svg>
                            }
                        </button>
                    </div>
                    @if (dropdownOpen() && channelQuery().trim().length >= 2) {
                        <div class="absolute top-full left-0 right-0 mt-1 z-30 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl shadow-black/60 max-h-80 overflow-y-auto"
                             (mousedown)="$event.preventDefault()">
                            @if (isSearching()) {
                                <div class="p-3 text-sm text-gray-400">Поиск…</div>
                            } @else if (searchSuggestions().length === 0) {
                                <div class="p-3 text-sm text-gray-400">Ничего не найдено</div>
                            } @else {
                                @for (s of searchSuggestions(); track s.id; let i = $index) {
                                    <button type="button"
                                            class="w-full flex items-center gap-3 p-2 text-left transition-colors"
                                            [class]="suggestionItemClass(s.id, i)"
                                            (click)="onSuggestionClick(s)">
                                        <img [src]="s.profileImageUrl" class="w-8 h-8 rounded-full object-cover flex-shrink-0" alt="">
                                        <div class="flex-1 min-w-0">
                                            <div class="text-sm text-white truncate">{{ s.displayName }}</div>
                                            <div class="text-xs text-gray-400 truncate">{{ '@' + s.login }}</div>
                                        </div>
                                        @if (isAlreadyAdded(s.id)) {
                                            <span class="text-[10px] text-gray-400 whitespace-nowrap">уже добавлен</span>
                                        }
                                    </button>
                                }
                            }
                        </div>
                    }
                </div>
                @if (channelError()) {
                    <p class="text-red-400 text-xs">{{ channelError() }}</p>
                }
            </div>

            <!-- Channels List. Below md: horizontal scrollable chip row
                 (fits the full-width stacked sidebar without eating vertical
                 space); at md+ identical to the original vertical list. -->
            <div class="flex-1 flex flex-row md:flex-col gap-2 md:gap-0 md:space-y-1 overflow-x-auto md:overflow-x-visible md:overflow-y-auto p-3 custom-scrollbar">
                @for (channel of savedChannels(); track channel.id) {
                    <div class="group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors shrink-0 w-40 md:w-auto"
                         [class]="selectedChannelId() === channel.id ? 'bg-indigo-900/40 border-l-4 border-indigo-500' : 'hover:bg-gray-800 border-l-4 border-transparent'"
                         (click)="selectChannel(channel)">
                         <div class="flex items-center gap-3 overflow-hidden">
                            <img [src]="channel.profileImageUrl" class="w-8 h-8 rounded-full object-cover">
                            <span class="font-medium text-sm text-gray-200 truncate">{{ channel.name }}</span>
                         </div>
                         <button (click)="removeChannel(channel.id, $event)" class="text-gray-500 hover:text-red-400 p-1 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" appTooltip="Удалить канал">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
                         </button>
                    </div>
                }
                @if (savedChannels().length === 0) {
                    <div class="text-center p-4 text-gray-500 text-sm shrink-0">Нет сохраненных каналов.</div>
                }
            </div>
        </div>

        <!-- Right Main Area: VODs Grid. min-h-0 is load-bearing below md: the
             panel is a flex-col there, and without it this item's min-height
             (auto) grows to the full grid height — the inner overflow-y-auto
             never gets a bounded height, the panel's overflow-hidden clips the
             rest, and touch scrolling is dead (scrollIntoView on open could
             still scroll the clipped panel, stranding the user mid-list). -->
        <div class="flex-1 min-h-0 flex flex-col bg-gray-900 relative">
            @if (selectedChannel()) {
                <!-- Channel Header & Settings -->
                <div class="p-3 md:p-6 border-b border-gray-800 bg-gray-950 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div class="flex items-center gap-3 md:gap-4">
                        <img [src]="selectedChannel()!.profileImageUrl" class="w-10 h-10 md:w-14 md:h-14 rounded-full border-2 border-indigo-500">
                        <div>
                            <h2 class="text-lg md:text-2xl font-bold text-white leading-tight">{{ selectedChannel()!.name }}</h2>
                            <p class="text-gray-400 text-sm">{{ subTabLabels[activeSubTab()] }}</p>
                        </div>
                        <button (click)="refreshActiveSubTab()"
                                [disabled]="activeIsLoadingVideos() || isLoadingTopClips()"
                                class="ml-2 p-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                                appTooltip="Обновить">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" [class.animate-spin]="activeIsLoadingVideos() || isLoadingTopClips()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Sub-tab bar -->
                <div role="tablist" aria-label="Тип контента канала" class="flex border-b border-gray-800 bg-gray-950 text-sm font-medium">
                    <button role="tab" [attr.aria-selected]="activeSubTab() === 'streams'" (click)="setActiveSubTab('streams')"
                            [class]="activeSubTab() === 'streams' ? 'text-indigo-400 border-b-2 border-b-indigo-500 bg-indigo-500/10' : 'text-indigo-400/50 hover:text-indigo-300 hover:bg-indigo-500/5'"
                            class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                        Стримы
                    </button>
                    <button role="tab" [attr.aria-selected]="activeSubTab() === 'clips'" (click)="setActiveSubTab('clips')"
                            [class]="activeSubTab() === 'clips' ? 'text-purple-400 border-b-2 border-b-purple-500 bg-purple-500/10' : 'text-purple-500/50 hover:text-purple-300 hover:bg-purple-500/5'"
                            class="flex-1 py-3 transition-colors border-r border-r-gray-800/70">
                        Клипы
                    </button>
                    <button role="tab" [attr.aria-selected]="activeSubTab() === 'highlights'" (click)="setActiveSubTab('highlights')"
                            [class]="activeSubTab() === 'highlights' ? 'text-amber-400 border-b-2 border-b-amber-500 bg-amber-500/10' : 'text-amber-500/50 hover:text-amber-300 hover:bg-amber-500/5'"
                            class="flex-1 py-3 transition-colors">
                        Хайлайты
                    </button>
                </div>

                <!-- Content Grid -->
                <div class="flex-1 overflow-y-auto overscroll-contain p-3 md:p-6 custom-scrollbar">

                  @if (activeSubTab() === 'clips') {
                    <!-- Period filter chips: rank by view_count over a date window via Helix started_at. -->
                    <div class="flex gap-2 mb-4 flex-wrap">
                       @for (p of ['24h', '7d', '30d', 'all']; track p) {
                          <button (click)="setTopClipsPeriod($any(p))"
                                  [class]="topClipsPeriod() === p
                                     ? 'px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-600 text-white border border-purple-400 shadow-md shadow-purple-900/50 transition-colors'
                                     : 'px-3 py-1.5 rounded-full text-xs font-medium bg-gray-800 text-gray-400 hover:text-purple-300 hover:bg-purple-900/30 border border-gray-700 transition-colors'">
                             {{ clipsPeriodLabels[$any(p)] }}
                          </button>
                       }
                    </div>
                    @if (isLoadingTopClips()) {
                         <div class="flex items-center justify-center h-full">
                             <svg class="animate-spin h-10 w-10 text-purple-500" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                         </div>
                    } @else if (topClipsError()) {
                          <div class="flex flex-col items-center justify-center h-full text-red-500">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-4 text-red-800/80" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <p class="font-bold">Ошибка загрузки клипов</p>
                              <p class="text-xs text-red-400 mt-2 max-w-sm text-center">{{ topClipsError() }}</p>
                          </div>
                    } @else if (topClips().length === 0) {
                          <div class="flex flex-col items-center justify-center h-full text-gray-500">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              @if (topClipsPeriod() === 'all') {
                                <p>У этого канала нет популярных клипов.</p>
                              } @else {
                                <p>За выбранный период клипов нет.</p>
                                <p class="text-[10px] not-italic mt-1">Попробуй другой период выше.</p>
                              }
                          </div>
                    } @else {
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            @for (clip of topClips(); track clip.id) {
                                <div class="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-purple-500 transition-all group flex flex-col h-full cursor-pointer relative"
                                     (click)="processClip(clip)">
                                     <!-- Thumbnail -->
                                     <div class="relative w-full aspect-video bg-gray-900 border-b border-gray-700">
                                          <img [src]="clip.thumbnail_url" class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity">
                                          <!-- View count overlay -->
                                          <div class="absolute top-2 left-2 bg-black/80 px-2 py-0.5 rounded text-xs font-mono font-bold text-purple-300 flex items-center gap-1"
                                               appTooltip="Просмотров">
                                             <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                             {{ formatViewCount(clip.view_count) }}
                                          </div>
                                          <!-- Duration -->
                                          <div class="absolute bottom-2 right-2 bg-black/80 px-2 py-0.5 rounded text-xs font-mono font-bold text-white">
                                             {{ formatClipDuration(clip.duration) }}
                                          </div>
                                          <!-- "В библиотеке" badge if VOD saved -->
                                          @if (clip.video_id && getProject(clip.video_id); as project) {
                                             <div class="absolute top-2 right-2 z-10 bg-indigo-500/90 px-1.5 py-1 rounded text-white shadow-lg flex items-center gap-1"
                                                  appTooltip="ВОД сохранён в библиотеке">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                                             </div>
                                          }
                                          <!-- "Без VOD" badge -->
                                          @if (!clip.video_id) {
                                             <div class="absolute top-2 right-2 z-10 bg-amber-600/80 px-2 py-0.5 rounded text-white shadow-lg text-[10px] font-bold tracking-wider"
                                                  appTooltip="ВОД недоступен (sub-only или удалён). Откроется оригинал клипа на Twitch.">
                                                БЕЗ ВОДА
                                             </div>
                                          }
                                     </div>
                                     <!-- Info -->
                                     <div class="p-3 flex flex-col flex-1">
                                         <h3 class="font-bold text-xs text-gray-100 line-clamp-2 leading-tight mb-2 group-hover:text-purple-400 transition-colors" [appTooltip]="clip.title">{{ clip.title }}</h3>
                                         <div class="mt-auto flex items-center justify-between text-[11px] text-gray-400">
                                             <span class="truncate text-purple-400/80" [appTooltip]="'Автор клипа: ' + clip.creator_name">{{ clip.creator_name }}</span>
                                             <span class="text-gray-500">{{ formatDate(clip.created_at) }}</span>
                                         </div>
                                     </div>
                                </div>
                            }
                        </div>
                    }
                  } @else {
                    <!-- streams + highlights — same template (TwitchVideoData shape) -->
                    @if (activeIsLoadingVideos()) {
                         <div class="flex items-center justify-center h-full">
                             <svg class="animate-spin h-10 w-10 text-indigo-500" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                         </div>
                    } @else if (activeVideosError()) {
                          <div class="flex flex-col items-center justify-center h-full text-red-500">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-4 text-red-800/80" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <p class="font-bold">Ошибка загрузки</p>
                              <p class="text-xs text-red-400 mt-2 max-w-sm text-center">{{ activeVideosError() }}</p>
                          </div>
                    } @else if (activeVideoList().length === 0) {
                          <div class="flex flex-col items-center justify-center h-full text-gray-500">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              @if (activeSubTab() === 'highlights') {
                                <p>У этого канала нет хайлайтов.</p>
                              } @else {
                                <p>Не найдено архивированных VOD'ов для этого канала.</p>
                              }
                          </div>
                    } @else {
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            @for (video of activeVideoList(); track video.id) {
                                <div class="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-indigo-500 transition-all group flex flex-col h-full cursor-pointer relative"
                                     [class]="getColorStripeClass(getProject(video.id)?.userColor)"
                                     [class.ring-4]="video.id === currentVideoId()"
                                     [class.ring-white]="video.id === currentVideoId()"
                                     [class.ring-offset-2]="video.id === currentVideoId()"
                                     [class.ring-offset-gray-900]="video.id === currentVideoId()"
                                     [class.shadow-lg]="video.id === currentVideoId()"
                                     [class.shadow-white/30]="video.id === currentVideoId()"
                                     [attr.id]="'lib-vod-' + video.id"
                                     (click)="processVideo(video)">

                                     <!-- Color dot picker — top-right corner of thumbnail, always visible -->
                                     <div class="absolute top-2 right-2 z-20" (click)="$event.stopPropagation()">
                                        <button type="button"
                                                (click)="toggleColorPicker(video.id, $event)"
                                                [class]="getColorDotClass(getProject(video.id)?.userColor)"
                                                [appTooltip]="getProject(video.id)?.userColor ? 'Сменить метку / снять' : 'Поставить цветную метку'">
                                        </button>
                                        @if (openColorPickerFor() === video.id) {
                                           <div class="absolute top-full right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl shadow-black/60 p-2 flex gap-1.5 z-30"
                                                (click)="$event.stopPropagation()">
                                              @for (c of colorPalette; track c.name) {
                                                 <button type="button"
                                                         (click)="pickColor(video, c.name, $event)"
                                                         [class]="'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ' + c.bg + ' ' + (getProject(video.id)?.userColor === c.name ? 'border-white' : 'border-transparent')"
                                                         [appTooltip]="c.hint">
                                                 </button>
                                              }
                                              <button type="button"
                                                      (click)="pickColor(video, null, $event)"
                                                      class="w-6 h-6 rounded-full border-2 border-gray-600 hover:border-red-400 hover:text-red-400 text-gray-500 flex items-center justify-center transition-colors"
                                                      appTooltip="Убрать метку">
                                                 <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                              </button>
                                           </div>
                                        }
                                     </div>

                                     <!-- Thumbnail -->
                                     <div class="relative w-full aspect-video bg-gray-900 border-b border-gray-700">
                                          <img [src]="formatThumbnailUrl(video.thumbnail_url)" class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity">
                                         
                                         <!-- Markers Badge -->
                                          @if (videoMarkerCounts().get(video.id); as markersCount) {
                                                <div class="absolute bottom-2 left-2 bg-black/80 px-2 py-0.5 rounded text-xs font-mono font-bold text-green-400 flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                                                    {{ markersCount }}
                                                </div>
                                          }

                                          @if (isVideoLive(video)) {
                                             <div class="absolute bottom-2 right-2 bg-red-600 px-2 py-0.5 rounded text-xs font-mono font-bold text-white flex items-center gap-1 shadow-lg shadow-red-900/50"
                                                  appTooltip="Стрим сейчас в эфире — чат будет дозагружаться по мере появления новых сообщений">
                                                <span class="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                                LIVE
                                             </div>
                                          } @else {
                                             <div class="absolute bottom-2 right-2 bg-black/80 px-2 py-0.5 rounded text-xs font-mono font-bold text-white">
                                                {{ video.duration }}
                                             </div>
                                          }
                                         @if (getProject(video.id); as project) {
                                             <div class="absolute top-2 left-2 z-10 bg-indigo-500/90 pl-1.5 pr-1 py-1 rounded text-white shadow-lg backdrop-blur-sm flex items-center gap-1"
                                                  [appTooltip]="project.lastModifiedAt ? 'Проект сохранён · ' + formatDateTime(project.lastModifiedAt) : 'Проект сохранён'">
                                                 <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                 @if (project.lastModifiedAt) {
                                                    <span class="font-mono text-[11px] leading-none">{{ formatDateTime(project.lastModifiedAt) }}</span>
                                                 }
                                                 <button (click)="deleteProject(video, $event)"
                                                         class="p-0.5 rounded text-white/70 hover:bg-red-500/80 hover:text-white transition-colors"
                                                         appTooltip="Удалить данные проекта">
                                                     <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                                 </button>
                                             </div>
                                         }
                                     </div>

                                     <!-- Info -->
                                     <div class="p-3 flex flex-col flex-1">
                                         <h3 class="font-bold text-xs text-gray-100 line-clamp-2 leading-tight mb-2 group-hover:text-indigo-400 transition-colors" [appTooltip]="video.title">{{ video.title }}</h3>
                                         <div class="mt-auto flex items-center justify-between text-[11px] text-gray-400">
                                             <span>{{ formatDate(video.created_at) }}</span>
                                             
                                             <!-- Download Status Indicator -->
                                             @if (downloadingVideos().has(video.id)) {
                                                 <span class="flex items-center gap-1 text-indigo-400 font-medium text-[10px]" appTooltip="Загрузка чата в фоне...">
                                                     <svg class="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                     <span>Загрузка...</span>
                                                 </span>
                                             } @else if (isChatDownloaded(video.id)) {
                                                 <span class="flex items-center gap-1 text-green-400 font-medium" appTooltip="Чат скачан (сохранен)">
                                                     <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                                                     <span>Чат скачан</span>
                                                 </span>
                                             } @else {
                                                <span class="text-gray-500 hover:text-white flex items-center gap-1 cursor-pointer" appTooltip="Загрузить чат в фоне" (click)="downloadChatInBackground(video, $event)">
                                                     <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                     <span>Загрузить</span>
                                                </span>
                                             }
                                         </div>
                                     </div>
                                </div>
                            }
                        </div>
                    }
                  }
                </div>
            } @else {
                <div class="flex flex-col items-center justify-center h-full text-gray-600">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                     <h2 class="text-2xl font-bold text-gray-700">Выберите канал</h2>
                     <p class="text-gray-500">Добавьте или выберите канал из списка слева, чтобы просмотреть стримы.</p>
                </div>
            }
        </div>

      </div>
    </div>
  `,
    styles: [`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #374151; border-radius: 20px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #4B5563; }
    .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
    @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
  `]
})
export class ChannelLibraryOverlayComponent {
    @Output() close = new EventEmitter<void>();
    @Output() loadVideo = new EventEmitter<string>(); // Emits Twitch Video ID or URL

    private libraryService = inject(LibraryService);
    private apiService = inject(TwitchApiService);
    private twitchChatService = inject(TwitchChatService);
    private twitchChatState = inject(TwitchChatStateService);
    private firebaseService = inject(FirebaseService);

    savedChannels = this.libraryService.savedChannels;
    downloadingVideos = signal<Set<string>>(new Set());
    videoMarkerCounts = signal<Map<string, number>>(new Map());

    selectedChannelId = signal<string | null>(null);

    selectedChannel = computed(() => {
        const id = this.selectedChannelId();
        return this.savedChannels().find(c => c.id === id) || null;
    });

    channelVideos = signal<TwitchVideoData[]>([]);
    isLoadingVideos = signal(false);
    isLoadingMore = signal(false);
    videosError = signal('');
    nextCursor = signal<string | null>(null);

    // Sub-tabs in the right pane: streams | top clips | highlights. Persisted only
    // for the lifetime of the overlay — re-opening defaults back to «Стримы».
    activeSubTab = signal<'streams' | 'clips' | 'highlights'>('streams');

    // Top clips (Helix /clips, top-100 by view_count). Period filter scopes via
    // started_at: 'all' = no filter, '24h'/'7d'/'30d' = recent. Loaded lazily on
    // sub-tab entry or period change; cached server-side 1h per (channel, period).
    topClips = signal<HelixClip[]>([]);
    isLoadingTopClips = signal(false);
    topClipsError = signal('');
    topClipsPeriod = signal<ClipsPeriod>('all');
    private loadedTopClipsKey: string | null = null; // `${login}:${period}`

    readonly clipsPeriodLabels: Record<ClipsPeriod, string> = {
        '24h': '24 часа',
        '7d':  '7 дней',
        '30d': '30 дней',
        'all': 'Всё время',
    };

    // Highlights (Helix /videos?type=highlight). Same shape as streams so we can
    // reuse the VOD card template.
    channelHighlights = signal<TwitchVideoData[]>([]);
    isLoadingHighlights = signal(false);
    highlightsError = signal('');
    private loadedHighlightsForChannel: string | null = null;

    // Sub-tab title bar uses these for the dynamic subtitle.
    readonly subTabLabels: Record<'streams' | 'clips' | 'highlights', string> = {
        streams: 'Стримы канала',
        clips: 'Клипы канала',
        highlights: 'Хайлайты канала',
    };

    // Unified getters for streams/highlights (TwitchVideoData) — both tabs render
    // the same card template, only the source signal differs. Clips use a
    // separate template (different shape).
    activeVideoList = computed(() =>
        this.activeSubTab() === 'highlights' ? this.channelHighlights() : this.channelVideos()
    );
    activeIsLoadingVideos = computed(() =>
        this.activeSubTab() === 'highlights' ? this.isLoadingHighlights() : this.isLoadingVideos()
    );
    activeVideosError = computed(() =>
        this.activeSubTab() === 'highlights' ? this.highlightsError() : this.videosError()
    );

    isFetchingChannel = signal(false);
    channelError = signal('');

    // Channel search autocomplete state. `channelQuery` is the controlled input value;
    // `searchSuggestions` holds the latest Helix /search/channels result (≤10 entries);
    // `dropdownOpen` gates render. `highlightedIndex = -1` means «no kbd selection» —
    // Enter then falls through to raw-text `addChannel()` (covers exact-login case
    // where /search prefix-match misses but /users finds).
    channelQuery = signal('');
    searchSuggestions = signal<ChannelSearchResult[]>([]);
    isSearching = signal(false);
    dropdownOpen = signal(false);
    highlightedIndex = signal(-1);
    private debounceTimer: any = null;
    private blurTimer: any = null;
    // Race-guard against in-flight searches: each `runSearch` captures a token,
    // and only commits its result if it's still the latest. Drops stale responses
    // when user types faster than the network resolves.
    private latestSearchToken = 0;

    // String form of the currently-loaded VOD's id, or null. Used to highlight
    // the matching card in the streams/highlights grids so the user can spot
    // «который сейчас открыт» without scanning all thumbnails.
    currentVideoId = computed(() => {
        const vid = this.twitchChatState.videoId();
        return vid ? String(vid) : null;
    });

    // --- Color tag picker state ---
    openColorPickerFor = signal<string | null>(null); // video.id currently showing its palette

    // Preset palette. Names are the persisted identifier; hints surface on hover as a default semantic nudge.
    // Users are free to assign their own meaning — the app doesn't enforce it.
    readonly colorPalette: Array<{ name: string; bg: string; hint: string }> = [
        { name: 'blue',   bg: 'bg-blue-500',   hint: 'Смотрю' },
        { name: 'amber',  bg: 'bg-amber-500',  hint: 'В очереди' },
        { name: 'green',  bg: 'bg-green-500',  hint: 'Готово' },
        { name: 'red',    bg: 'bg-red-500',    hint: 'Срочно' },
        { name: 'purple', bg: 'bg-purple-500', hint: 'Любимое' },
    ];

    // Stripe classes — full class strings so Tailwind CDN JIT parser finds them.
    private readonly colorStripeMap: Record<string, string> = {
        blue:   'border-l-4 border-l-blue-500',
        amber:  'border-l-4 border-l-amber-500',
        green:  'border-l-4 border-l-green-500',
        red:    'border-l-4 border-l-red-500',
        purple: 'border-l-4 border-l-purple-500',
    };
    private readonly colorDotMap: Record<string, string> = {
        blue:   'bg-blue-500 border-blue-300',
        amber:  'bg-amber-500 border-amber-300',
        green:  'bg-green-500 border-green-300',
        red:    'bg-red-500 border-red-300',
        purple: 'bg-purple-500 border-purple-300',
    };

    getColorStripeClass(color: string | null | undefined): string {
        if (!color) return '';
        return this.colorStripeMap[color] ?? '';
    }

    getColorDotClass(color: string | null | undefined): string {
        const base = 'w-5 h-5 rounded-full border-2 shadow-md transition-transform hover:scale-110';
        if (!color) {
            return base + ' bg-black/40 border-white/50 hover:border-white backdrop-blur-sm';
        }
        return base + ' ' + (this.colorDotMap[color] ?? 'bg-gray-500 border-gray-300');
    }

    toggleColorPicker(videoId: string, event: Event) {
        event.stopPropagation();
        this.openColorPickerFor.update(current => current === videoId ? null : videoId);
    }

    pickColor(video: TwitchVideoData, color: string | null, event: Event) {
        event.stopPropagation();
        this.libraryService.setProjectColor({
            videoId: video.id,
            channelId: this.selectedChannelId() ?? '',
            title: video.title,
            createdAt: video.created_at,
            duration: video.duration,
            thumbnailUrl: video.thumbnail_url,
        }, color);
        this.openColorPickerFor.set(null);
    }

    onDocumentClick() {
        // Closes the picker on any outside click. Button & palette stopPropagation themselves.
        // Search dropdown closes via input `(blur)` instead — host-listener at document
        // doesn't fire on panel-internal clicks (panel stopPropagation), so blur is the
        // reliable signal that focus left the input.
        if (this.openColorPickerFor()) this.openColorPickerFor.set(null);
    }

    onEscape() {
        if (this.openColorPickerFor()) this.openColorPickerFor.set(null);
        if (this.dropdownOpen()) this.closeDropdown();
    }

    constructor() {
        // Default channel selection on library open. Smart: if a project is
        // currently loaded, jump straight to its channel — saves the user from
        // scanning the saved-channels list to find «what am I viewing». Falls
        // back to the first saved channel if no project is open or its channel
        // is no longer in the library.
        effect(() => {
            const channels = this.savedChannels();
            if (channels.length === 0) return;
            if (this.selectedChannelId()) return;

            const currentVid = this.twitchChatState.videoId();
            if (currentVid) {
                const project = this.libraryService.getProject(String(currentVid));
                if (project) {
                    const channel = channels.find(c => c.id === project.channelId);
                    if (channel) {
                        this.selectChannel(channel);
                        return;
                    }
                }
            }

            this.selectChannel(channels[0]);
        });

        effect(() => {
            const channel = this.selectedChannel();
            if (channel) {
                this.fetchVideosForChannel(channel.login);
            } else {
                this.channelVideos.set([]);
            }
        });

        // Lazy-fetch top clips / highlights when the user enters the corresponding
        // sub-tab for the first time on a given (channel, period). Cached per-key
        // so re-entering or toggling chips for already-loaded combos is instant.
        effect(() => {
            const channel = this.selectedChannel();
            const tab = this.activeSubTab();
            const period = this.topClipsPeriod();
            if (!channel) return;
            if (tab === 'clips') {
                const key = `${channel.login}:${period}`;
                if (this.loadedTopClipsKey !== key) {
                    this.fetchTopClips(channel.login, period);
                }
            } else if (tab === 'highlights' && this.loadedHighlightsForChannel !== channel.login) {
                this.fetchChannelHighlights(channel.login);
            }
        });
    }

    setActiveSubTab(tab: 'streams' | 'clips' | 'highlights') {
        this.activeSubTab.set(tab);
    }

    setTopClipsPeriod(period: ClipsPeriod) {
        this.topClipsPeriod.set(period);
    }

    onQueryInput(ev: Event) {
        const value = (ev.target as HTMLInputElement).value;
        this.channelQuery.set(value);
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null; }
        const trimmed = value.trim();
        if (trimmed.length < 2) {
            this.searchSuggestions.set([]);
            this.isSearching.set(false);
            this.highlightedIndex.set(-1);
            return;
        }
        this.dropdownOpen.set(true);
        this.debounceTimer = setTimeout(() => this.runSearch(trimmed), 300);
    }

    onInputFocus() {
        if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
        if (this.channelQuery().trim().length >= 2) {
            this.dropdownOpen.set(true);
        }
    }

    // 150ms delay so a click on a dropdown item registers BEFORE the input loses
    // focus and closes the dropdown. Items also `(mousedown).preventDefault()`,
    // so blur often won't fire at all when clicking inside — this is the
    // belt-and-suspenders fallback.
    onInputBlur() {
        this.blurTimer = setTimeout(() => {
            this.dropdownOpen.set(false);
            this.highlightedIndex.set(-1);
            this.blurTimer = null;
        }, 150);
    }

    private async runSearch(q: string) {
        const token = ++this.latestSearchToken;
        this.isSearching.set(true);
        try {
            const results = await this.apiService.searchChannels(q);
            if (token === this.latestSearchToken) {
                this.searchSuggestions.set(results);
                this.highlightedIndex.set(-1);
            }
        } catch (err) {
            console.error('Channel search failed:', err);
            if (token === this.latestSearchToken) {
                this.searchSuggestions.set([]);
            }
        } finally {
            if (token === this.latestSearchToken) {
                this.isSearching.set(false);
            }
        }
    }

    onQueryKeydown(ev: KeyboardEvent) {
        const len = this.searchSuggestions().length;
        switch (ev.key) {
            case 'ArrowDown':
                if (len > 0) {
                    ev.preventDefault();
                    this.highlightedIndex.update(i => Math.min(i + 1, len - 1));
                }
                break;
            case 'ArrowUp':
                if (len > 0) {
                    ev.preventDefault();
                    this.highlightedIndex.update(i => Math.max(i - 1, -1));
                }
                break;
            case 'Enter': {
                const idx = this.highlightedIndex();
                const sug = this.searchSuggestions();
                if (idx >= 0 && sug[idx]) {
                    ev.preventDefault();
                    this.onSuggestionClick(sug[idx]);
                } else {
                    ev.preventDefault();
                    const q = this.channelQuery().trim();
                    if (q) {
                        this.addChannel(q);
                        this.closeDropdown();
                    }
                }
                break;
            }
            case 'Escape':
                ev.preventDefault();
                this.closeDropdown();
                break;
        }
    }

    onSuggestionClick(s: ChannelSearchResult) {
        if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
        if (this.isAlreadyAdded(s.id)) {
            const existing = this.libraryService.savedChannels().find(c => c.id === s.id);
            if (existing) this.selectChannel(existing);
            this.closeDropdown();
            return;
        }
        // /search/channels' `thumbnail_url` ≡ /users' `profile_image_url` empirically
        // (same Twitch CDN). If they ever diverge, `getChannelVideos` overwrites on
        // first VOD list fetch.
        const newChannel: SavedChannel = {
            id: s.id,
            name: s.displayName,
            login: s.login,
            profileImageUrl: s.profileImageUrl,
        };
        this.libraryService.addOrUpdateChannel(newChannel);
        this.selectChannel(newChannel);
        this.closeDropdown();
    }

    closeDropdown() {
        this.dropdownOpen.set(false);
        this.channelQuery.set('');
        this.searchSuggestions.set([]);
        this.highlightedIndex.set(-1);
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null; }
        if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
    }

    isAlreadyAdded(id: string): boolean {
        return this.libraryService.savedChannels().some(c => c.id === id);
    }

    suggestionItemClass(id: string, index: number): string {
        if (this.isAlreadyAdded(id)) return 'opacity-50 cursor-default';
        return this.highlightedIndex() === index
            ? 'bg-gray-700/60'
            : 'hover:bg-gray-700/60';
    }

    async addChannel(name: string) {
        if (!name.trim()) return;
        this.isFetchingChannel.set(true);
        this.channelError.set('');
        try {
            const data = await this.apiService.getChannelVideos(name.trim());
            const newChannel: SavedChannel = {
                id: data.channel.id,
                name: data.channel.name,
                login: data.channel.login,
                profileImageUrl: data.channel.profileImageUrl
            };
            this.libraryService.addOrUpdateChannel(newChannel);
            this.selectChannel(newChannel);
        } catch (err: any) {
            this.channelError.set(err.message || 'Ошибка загрузки канала');
        } finally {
            this.isFetchingChannel.set(false);
        }
    }

    removeChannel(id: string, event: Event) {
        event.stopPropagation();
        if (confirm('Удалить канал из библиотеки?')) {
            this.libraryService.removeChannel(id);
            if (this.selectedChannelId() === id) {
                this.selectedChannelId.set(null);
            }
        }
    }

    selectChannel(channel: SavedChannel) {
        this.selectedChannelId.set(channel.id);
    }

    async refreshVideos() {
        const channel = this.selectedChannel();
        if (!channel) return;
        await this.fetchVideosForChannel(channel.login, true);
    }

    /** Refresh whichever sub-tab is active. Replaces refreshVideos for the new UI. */
    async refreshActiveSubTab() {
        const channel = this.selectedChannel();
        if (!channel) return;
        switch (this.activeSubTab()) {
            case 'clips':
                await this.fetchTopClips(channel.login, this.topClipsPeriod(), true);
                break;
            case 'highlights':
                await this.fetchChannelHighlights(channel.login, true);
                break;
            default:
                await this.fetchVideosForChannel(channel.login, true);
        }
    }

    private async fetchTopClips(login: string, period: ClipsPeriod, forceRefresh = false) {
        this.isLoadingTopClips.set(true);
        this.topClipsError.set('');
        try {
            const data = await this.apiService.getChannelClips(login, period, forceRefresh);
            this.topClips.set(data.clips);
            this.loadedTopClipsKey = `${login}:${period}`;
        } catch (err: any) {
            console.error('Top clips fetch failed:', err);
            this.topClipsError.set(err.message || 'Не удалось загрузить клипы канала.');
            this.topClips.set([]);
        } finally {
            this.isLoadingTopClips.set(false);
        }
    }

    private async fetchChannelHighlights(login: string, forceRefresh = false) {
        this.isLoadingHighlights.set(true);
        this.highlightsError.set('');
        try {
            const data = await this.apiService.getChannelHighlights(login, undefined, forceRefresh);
            this.channelHighlights.set(data.videos);
            this.loadedHighlightsForChannel = login;
            await this.fetchMarkersForVideos(data.videos);
            this.scrollCurrentProjectIntoView(data.videos);
        } catch (err: any) {
            console.error('Highlights fetch failed:', err);
            this.highlightsError.set(err.message || 'Не удалось загрузить хайлайты канала.');
            this.channelHighlights.set([]);
        } finally {
            this.isLoadingHighlights.set(false);
        }
    }

    /**
     * Click on a top-clip card. If the clip has a backing VOD that's still
     * available (`video_id` non-null), open it via the standard loadFromUrl
     * pipeline AND stash `vod_offset` so the player seeks straight to the clip
     * after Twitch.Player.READY. Project auto-creates inside the load flow
     * (libraryService.saveProject during finalizeStreamedDownload).
     *
     * If the clip has no VOD (sub-only stream or live-pending clip), the only
     * way to view it is the original Twitch clip page — open in a new tab.
     */
    processClip(clip: HelixClip) {
        if (clip.video_id) {
            this.twitchChatState.pendingSeekAfterLoad.set(clip.vod_offset);
            this.loadVideo.emit(`https://www.twitch.tv/videos/${clip.video_id}`);
        } else {
            window.open(clip.url, '_blank', 'noopener');
        }
    }

    formatViewCount(n: number): string {
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
        return String(n);
    }

    formatClipDuration(sec: number): string {
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${String(s).padStart(2, '0')}`;
    }

    private async fetchVideosForChannel(login: string, forceRefresh = false) {
        this.isLoadingVideos.set(true);
        this.videosError.set('');
        this.nextCursor.set(null);
        try {
            const data = await this.apiService.getChannelVideos(login, undefined, forceRefresh);
            this.channelVideos.set(data.videos);
            this.nextCursor.set(data.pagination?.cursor || null);
            await this.fetchMarkersForVideos(data.videos);
            this.scrollCurrentProjectIntoView(data.videos);
        } catch (err: any) {
            console.error(err);
            this.videosError.set(err.message || 'Произошла неизвестная ошибка при обращении к серверу.');
            this.channelVideos.set([]);
        } finally {
            this.isLoadingVideos.set(false);
        }
    }

    /** Scroll the card matching the currently-loaded VOD into view, if it's in
     *  the just-loaded list. Two rAFs because Angular needs to flush the @for
     *  before the [attr.id] anchors land in the DOM. block:'center' so the
     *  card sits midway, not flush against the top edge. */
    private scrollCurrentProjectIntoView(videos: TwitchVideoData[]) {
        const currentVid = this.currentVideoId();
        if (!currentVid) return;
        if (!videos.some(v => v.id === currentVid)) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = document.getElementById('lib-vod-' + currentVid);
                if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            });
        });
    }

    async loadMoreVideos() {
        const cursor = this.nextCursor();
        const channel = this.selectedChannel();
        if (!cursor || !channel || this.isLoadingMore()) return;

        this.isLoadingMore.set(true);
        try {
            const data = await this.apiService.getChannelVideos(channel.login, cursor);
            // Append new videos to the existing list
            this.channelVideos.update(videos => [...videos, ...data.videos]);
            this.nextCursor.set(data.pagination?.cursor || null);
            await this.fetchMarkersForVideos(data.videos);
        } catch (err: any) {
            console.error('Ошибка при загрузке дополнительных видео:', err);
            // Non-fatal error for the user, just log or add a toast later
        } finally {
            this.isLoadingMore.set(false);
        }
    }

    parseTwitchDuration(duration: string): number {
        const match = duration.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
        if (!match) return 0;
        const h = parseInt(match[1] || '0', 10);
        const m = parseInt(match[2] || '0', 10);
        const s = parseInt(match[3] || '0', 10);
        return h * 3600 + m * 60 + s;
    }

    async fetchMarkersForVideos(videos: TwitchVideoData[]) {
        if (!videos.length || !this.firebaseService.isInitialized()) return;

        let minTime = Number.MAX_SAFE_INTEGER;
        let maxTime = 0;

        for (const v of videos) {
            const startMs = new Date(v.created_at).getTime();
            const durationSec = this.parseTwitchDuration(v.duration);
            const endMs = startMs + (durationSec + 3600) * 1000;

            if (startMs < minTime) minTime = startMs;
            if (endMs > maxTime) maxTime = endMs;
        }

        try {
            const allMarkers = await this.firebaseService.fetchStreamMarkers(new Date(minTime), new Date(maxTime));

            this.videoMarkerCounts.update(map => {
                const newMap = new Map(map);

                for (const v of videos) {
                    const startMs = new Date(v.created_at).getTime();
                    const durationSec = this.parseTwitchDuration(v.duration);
                    const endMs = startMs + (durationSec + 3600) * 1000;

                    const count = allMarkers.filter(m => {
                        const mTime = new Date(m.timestamp).getTime();
                        return mTime >= startMs && mTime <= endMs;
                    }).length;

                    if (count > 0) {
                        newMap.set(v.id, count);
                    }
                }
                return newMap;
            });
        } catch (err) {
            console.error('Error fetching stream markers for library:', err);
        }
    }

    formatThumbnailUrl(url: string): string {
        return url.replace('%{width}', '640').replace('%{height}', '360');
    }

    // Helix /videos returns `thumbnail_url: ""` (empty string) ONLY while the VOD's
    // source stream is currently being recorded; once the broadcast ends and the VOD
    // finalizes, Twitch fills it with the standard preview pattern. This is the
    // cheapest live-detect we can do at list-render time without a per-card backend
    // round-trip — actual live-poll machinery still confirms via /get-vod-status
    // before entering the chat-poll loop, so a false positive here only costs the
    // user a misleading badge until they open the VOD.
    isVideoLive(video: any): boolean {
        const url = video?.thumbnail_url;
        return !url || url === '';
    }

    formatDate(dateStr: string): string {
        return new Date(dateStr).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    formatDateTime(timestamp: number): string {
        return new Date(timestamp).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getProject(videoId: string): StreamProject | undefined {
        return this.libraryService.getProject(videoId);
    }

    isChatDownloaded(videoId: string): boolean {
        const project = this.libraryService.getProject(videoId);
        return project ? project.isChatDownloaded : false;
    }

    processVideo(video: TwitchVideoData) {
        // 1. Initialise/Save Project immediately
        const channel = this.selectedChannel();
        if (channel) {
            const project: StreamProject = this.libraryService.getProject(video.id) || {
                videoId: video.id,
                channelId: channel.id,
                title: video.title,
                createdAt: video.created_at,
                duration: video.duration,
                thumbnailUrl: video.thumbnail_url,
                isChatDownloaded: false,
                lastModifiedAt: Date.now()
            };
            this.libraryService.saveProject(project);

            // 2. Emit load action back to AppComponent
            // Provide direct video URL for app to process
            this.loadVideo.emit(`https://www.twitch.tv/videos/${video.id}`);
        }
    }

    async deleteProject(video: TwitchVideoData, event: Event) {
        event.stopPropagation();
        if (confirm(`Вы уверены, что хотите удалить сохраненные данные проекта для "${video.title}"?\nЭто действие очистит локальный кэш чата и расстановку хайлайтов.`)) {
            await this.libraryService.deleteProject(video.id);
        }
    }

    async downloadChatInBackground(video: TwitchVideoData, event: Event) {
        event.stopPropagation();
        const channel = this.selectedChannel();
        if (!channel) return;

        this.downloadingVideos.update(set => {
            const newSet = new Set(set);
            newSet.add(video.id);
            return newSet;
        });

        try {
            await this.twitchChatService.fetchAndSaveChatBackground(`https://www.twitch.tv/videos/${video.id}`, video.id, channel.login, video);
        } catch (e: any) {
            console.error('Ошибка фоновой загрузки:', e);
            alert(`Ошибка при загрузке чата для "${video.title}":\n${e.message}`);
        } finally {
            this.downloadingVideos.update(set => {
                const newSet = new Set(set);
                newSet.delete(video.id);
                return newSet;
            });
            // Обновляем ссылку на массив видео, чтобы Angular перерисовал интерфейс
            this.channelVideos.update(videos => [...videos]);
        }
    }
}
